import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"

import Text from "components/Text/Text"
import { Guid, IApiLink } from "@/types"
import useAssetVersions from "../hooks/useAssetVersions"
import VersionCollapsible from "../VersionCollapsible"
import AssetMaintenances from "components/AssetMaintenances/AssetMaintenances"
import toastMessages from "stores/toastMessages"

interface IAssetMaintenancesWithVersionsProps {
    assetId: Guid
    assetOperationHours?: number | null
    linkCreateAssetMaintenance?: IApiLink
    linkGetAssetVersions?: IApiLink
    currentAssetVersion?: number
    isCurrentVersion?: boolean
}

const AssetMaintenancesWithVersions: FC<IAssetMaintenancesWithVersionsProps> = ({
    assetId,
    assetOperationHours,
    linkCreateAssetMaintenance,
    linkGetAssetVersions,
    currentAssetVersion,
    isCurrentVersion = true,
}) => {
    const { t } = useTranslation()
    const { assetVersions } = useAssetVersions(linkGetAssetVersions, toastMessages.showSystemError)

    // Sort versions: current first, then by version number descending
    const sortedVersions = useMemo(() => {
        if (!assetVersions) return null
        return [...assetVersions].sort((a, b) => {
            if (a.isCurrentVersion) return -1
            if (b.isCurrentVersion) return 1
            return b.version - a.version
        })
    }, [assetVersions])

    // Find the version being viewed
    const viewedVersion = useMemo(() => {
        if (!assetVersions || currentAssetVersion === undefined) return null
        return assetVersions.find(v => v.version === currentAssetVersion)
    }, [assetVersions, currentAssetVersion])

    // If viewing a previous version, only show that version's content (no version dropdowns)
    if (!isCurrentVersion) {
        return (
            <AssetMaintenances
                assetId={viewedVersion?.assetId ?? assetId}
                assetOperationHours={undefined}
                linkCreateAssetMaintenance={undefined} // Don't allow creating for old versions
            />
        )
    }

    // If no versions available or just one version, show the regular AssetMaintenances
    if (!linkGetAssetVersions || !sortedVersions || sortedVersions.length <= 1) {
        return (
            <AssetMaintenances
                assetId={assetId}
                assetOperationHours={assetOperationHours}
                linkCreateAssetMaintenance={linkCreateAssetMaintenance}
            />
        )
    }

    // Get previous versions (non-current)
    const previousVersions = sortedVersions.filter(v => !v.isCurrentVersion)

    return (
        <>
            {/* Current version content shown directly without dropdown */}
            <AssetMaintenances
                assetId={assetId}
                assetOperationHours={assetOperationHours}
                linkCreateAssetMaintenance={linkCreateAssetMaintenance}
            />

            {/* Previous versions shown as collapsible dropdowns */}
            {previousVersions.length > 0 && (
                <div className="mt-4">
                    <Text type="h4" className="mb-2 color-grey-600">{t("asset.previousVersions")}</Text>
                    {previousVersions.map((version) => (
                        <VersionCollapsible
                            key={version.assetId}
                            version={version}
                            defaultExpanded={false}
                        >
                            {(isExpanded) => isExpanded && (
                                <AssetMaintenances
                                    assetId={version.assetId}
                                    assetOperationHours={undefined}
                                    linkCreateAssetMaintenance={undefined}
                                    hideTitle
                                />
                            )}
                        </VersionCollapsible>
                    ))}
                </div>
            )}
        </>
    )
}

export default AssetMaintenancesWithVersions
